import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import withStyles, {
  WithStyles,
  StyleRulesCallback
} from '@material-ui/core/styles/withStyles';

import {
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  IconButton
} from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';

import RegisterUserProvider, {
  RegisterUserFn
} from '../providers/RegisterUserProvider';
import {
  firebaseLogin,
  firebaseSignup,
  firebaseRecoverPassword
} from '../services';
import firebaseAnonymousSignup from '../services/firebaseAnonymousSignup';

const styles: StyleRulesCallback = theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    height: '100vh',
    backgroundColor: theme.palette.primary.main,
    justifyContent: 'center',
    alignItems: 'center'
  },
  container: {
    flexBasis: 440,
    margin: '32px 0 80px'
  },
  title: {
    fontSize: 30,
    marginBottom: 16,
    fontWeight: 400,
    textAlign: 'center'
  },
  body: {
    borderRadius: 0,
    padding: theme.spacing.unit * 5
  },
  primaryButton: {
    textTransform: 'none',
    borderRadius: 0,
    border: `1px solid ${theme.palette.primary.main}`,
    color: '#000',
    fontSize: 16
  },
  secondaryButton: {
    textTransform: 'none',
    borderRadius: 0,
    fontSize: 16
  },
  dialogFooter: {
    margin: '32px -20px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  close: {
    width: theme.spacing.unit * 4,
    height: theme.spacing.unit * 4
  }
});

interface Props {
  mode?: 'login' | 'signup' | null;
}

interface State {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  loading: boolean;
  showRecoverPasswordDialog: boolean;
  recoverEmail: string;
  snackbar: string | null;
}

class Authentication extends React.Component<
  Props & WithStyles & RouteComponentProps<any>,
  State
> {
  state = {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    loading: false,
    showRecoverPasswordDialog: false,
    recoverEmail: '',
    snackbar: null
  };

  handleLogin = async (event: any) => {
    event.preventDefault();
    this.setState({ loading: true });
    const { email, password } = this.state;
    try {
      await firebaseLogin(email, password);
      this.props.history.push('/');
    } catch (err) {
      console.error(err);
      this.setState({
        snackbar: 'Contraseña o email inválidos'
      });
    }
    this.setState({ loading: false });
  };

  handleSignup = async (event: any, registerUserFn: RegisterUserFn) => {
    event.preventDefault();
    this.setState({ loading: true });
    const { email, password, firstName, lastName } = this.state;
    try {
      await firebaseSignup(email, password);
      registerUserFn({ firstName, lastName });
    } catch (err) {
      console.error(err);
      this.setState({
        snackbar: 'El usuario ya existe',
        loading: false
      });
    }
  };

  handleAnonymousSignup = async (event: any) => {
    event.preventDefault();
    this.setState({ loading: true });
    try {
      await firebaseAnonymousSignup();
    } catch (err) {
      console.error(err);
      this.setState({
        snackbar: 'Problemas para continuar de forma anónima',
        loading: false
      });
    }
  };

  handleRecoverPassword = async (event: any) => {
    event.preventDefault();
    this.setState({ loading: true });
    const { recoverEmail } = this.state;
    try {
      await firebaseRecoverPassword(recoverEmail);
      this.setState({
        snackbar: 'Email enviado correctamente'
      });
    } catch (err) {
      console.error(err);
      this.setState({
        snackbar: 'Email inválido'
      });
    }
    this.setState({
      loading: false,
      showRecoverPasswordDialog: false
    });
  };

  renderRecoverPasswordDialog() {
    const { classes } = this.props;
    return (
      <Dialog
        open={this.state.showRecoverPasswordDialog}
        onClose={() => this.setState({ showRecoverPasswordDialog: false })}
      >
        <DialogTitle>Restaurar contraseña</DialogTitle>
        <DialogContent style={{ paddingBottom: 0 }}>
          <DialogContentText>
            Ingresá tu email y te enviaremos las instrucciones para restaurar tu
            contraseña
          </DialogContentText>
          <form
            action="#"
            onSubmit={event => this.handleRecoverPassword(event)}
          >
            <TextField
              autoFocus={true}
              fullWidth={true}
              required={true}
              margin="dense"
              label="Email"
              type="email"
              value={this.state.recoverEmail}
              onChange={e => this.setState({ recoverEmail: e.target.value })}
              disabled={this.state.loading}
            />

            <div className={classes.dialogFooter}>
              <Button
                color="default"
                className={classes.secondaryButton}
                style={{ margin: '0 4px' }}
                onClick={() =>
                  this.setState({ showRecoverPasswordDialog: false })
                }
                disabled={this.state.loading}
              >
                Cancelar
              </Button>
              <Button
                color="primary"
                className={classes.primaryButton}
                style={{ margin: '0 4px' }}
                disabled={this.state.loading}
                type="submit"
              >
                Enviar email
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  renderLoginForm() {
    const { classes } = this.props;
    return (
      <form action="#" onSubmit={event => this.handleLogin(event)}>
        <Typography variant="display1" className={classes.title}>
          Ingresar a Pirwa
        </Typography>

        <TextField
          fullWidth={true}
          required={true}
          name="email"
          type="email"
          label="Email"
          margin="dense"
          value={this.state.email}
          onChange={e => this.setState({ email: e.target.value })}
          disabled={this.state.loading}
        />

        <TextField
          fullWidth={true}
          required={true}
          name="password"
          type="password"
          label="Contraseña"
          margin="dense"
          value={this.state.password}
          onChange={e => this.setState({ password: e.target.value })}
          disabled={this.state.loading}
        />

        <Button
          type="submit"
          color="primary"
          className={classes.primaryButton}
          style={{ marginTop: 32 }}
          disabled={this.state.loading}
          fullWidth={true}
        >
          Ingresar
        </Button>

        <Button
          color="default"
          className={classes.secondaryButton}
          style={{ marginTop: 8 }}
          disabled={this.state.loading}
          fullWidth={true}
          onClick={() => this.setState({ showRecoverPasswordDialog: true })}
        >
          Olvidé mi contraseña
        </Button>

        <Button
          color="default"
          className={classes.secondaryButton}
          style={{ marginTop: 8 }}
          disabled={this.state.loading}
          fullWidth={true}
          onClick={() => this.props.history.push('/signup')}
        >
          No tengo una cuenta
        </Button>
      </form>
    );
  }

  renderSignupForm() {
    const { classes } = this.props;
    return (
      <RegisterUserProvider
        onCompleted={() => {
          this.setState({ loading: false });
          this.props.history.push('/');
        }}
      >
        {(registerUserFn: RegisterUserFn, loading: boolean) => (
          <form
            action="#"
            onSubmit={event => this.handleSignup(event, registerUserFn)}
          >
            <Typography variant="display1" className={classes.title}>
              Registrarse en Pirwa
            </Typography>

            <TextField
              fullWidth={true}
              required={true}
              name="firstName"
              label="Nombre"
              margin="dense"
              value={this.state.firstName}
              onChange={e => this.setState({ firstName: e.target.value })}
              disabled={this.state.loading}
            />

            <TextField
              fullWidth={true}
              required={true}
              name="lastName"
              label="Apellido"
              margin="dense"
              value={this.state.lastName}
              onChange={e => this.setState({ lastName: e.target.value })}
              disabled={this.state.loading}
            />

            <TextField
              fullWidth={true}
              required={true}
              name="email"
              type="email"
              label="Email"
              margin="dense"
              value={this.state.email}
              onChange={e => this.setState({ email: e.target.value })}
              disabled={this.state.loading}
            />

            <TextField
              fullWidth={true}
              required={true}
              name="password"
              type="password"
              label="Contraseña"
              margin="dense"
              value={this.state.password}
              onChange={e => this.setState({ password: e.target.value })}
              disabled={this.state.loading}
            />

            <Button
              type="submit"
              color="primary"
              className={classes.primaryButton}
              style={{ marginTop: 24 }}
              disabled={this.state.loading}
              fullWidth={true}
            >
              Registrarse
            </Button>

            <Button
              color="default"
              className={classes.secondaryButton}
              style={{ marginTop: 8 }}
              disabled={this.state.loading}
              fullWidth={true}
              onClick={() => this.props.history.push('/login')}
            >
              Ya tengo una cuenta
            </Button>
          </form>
        )}
      </RegisterUserProvider>
    );
  }

  renderOptions() {
    const { classes } = this.props;
    return (
      <>
        <Typography variant="display1" className={classes.title}>
          Bienvenido a Pirwa
        </Typography>

        <Button
          color="default"
          className={classes.secondaryButton}
          style={{ marginTop: 8 }}
          disabled={this.state.loading}
          fullWidth={true}
          onClick={() => this.props.history.push('/signup')}
        >
          Quiero crear mi cuenta
        </Button>

        <Button
          color="default"
          className={classes.secondaryButton}
          style={{ marginTop: 8 }}
          disabled={this.state.loading}
          fullWidth={true}
          onClick={() => this.props.history.push('/login')}
        >
          Ya tengo una cuenta
        </Button>

        <Button
          color="default"
          className={classes.secondaryButton}
          style={{ marginTop: 8 }}
          disabled={this.state.loading}
          fullWidth={true}
          onClick={this.handleAnonymousSignup}
        >
          Registrarme mas tarde
        </Button>
      </>
    );
  }

  renderForm() {
    const { classes, mode } = this.props;
    return (
      <div className={classes.container}>
        <Paper className={classes.body}>
          {mode === 'login' && this.renderLoginForm()}
          {mode === 'signup' && this.renderSignupForm()}
          {!mode && this.renderOptions()}
        </Paper>
        {this.renderRecoverPasswordDialog()}
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={!!this.state.snackbar}
          autoHideDuration={6000}
          onClose={() => this.setState({ snackbar: null })}
          message={<span>{this.state.snackbar || ''}</span>}
          action={
            <IconButton
              color="inherit"
              className={classes.close}
              onClick={() => this.setState({ snackbar: null })}
            >
              <CloseIcon />
            </IconButton>
          }
        />
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    return <main className={classes.root}>{this.renderForm()}</main>;
  }
}

export default withStyles(styles)(withRouter(Authentication));



// WEBPACK FOOTER //
// ./src/containers/Authentication.tsx